import { useMemo, useState } from "react";
import { timelineCategories, timelineItems, type TimelineItem } from "@/mocks/gossip";
import { getStateCfg } from "./stateStyle";

export default function UpdateTimeline() {
  const [category, setCategory] = useState("全部");
  const [visible, setVisible] = useState(5);
  const [expanded, setExpanded] = useState<string | null>(null);

  const list = useMemo(
    () => (category === "全部" ? timelineItems : timelineItems.filter((i) => i.category === category)),
    [category]
  );

  const shown = list.slice(0, visible);

  const toggleExpand = (id: string) => setExpanded((v) => (v === id ? null : id));

  return (
    <section id="timeline" className="bg-background-50 py-10 md:py-14">
      <div className="mx-auto w-full px-4 md:px-6">
        {/* section head */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-secondary-500">
              快讯流 · 按时间倒序
            </p>
            <h2 className="font-display text-2xl font-black text-foreground-950 md:text-3xl">
              九色黑料<span className="text-primary-600">最新进展</span>
            </h2>
            <p className="mt-1 max-w-xl text-sm text-foreground-600">
              新料、反转、辟谣全部按时间线归位；点条目可展开完整关联脉络，无需来回翻旧帖。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
            {timelineCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCategory(c);
                  setVisible(5);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                  category === c
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* dense timeline list */}
        <div className="divide-y divide-background-200 border-y border-background-200">
          {shown.map((item) => (
            <TimelineRow key={item.id} item={item} expanded={expanded === item.id} onToggle={() => toggleExpand(item.id)} />
          ))}
        </div>

        <div className="mt-5 flex items-center justify-center">
          {visible < list.length ? (
            <button
              type="button"
              onClick={() => setVisible((v) => v + 5)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-foreground-950"
            >
              <i className="ri-arrow-down-line text-sm"></i>
              再往下看 {list.length - visible} 条更新
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-xs text-secondary-500">
              <i className="ri-check-line"></i> 本页快讯已全部加载
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function TimelineRow({
  item,
  expanded,
  onToggle,
}: {
  item: TimelineItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const s = getStateCfg(item.state);
  return (
    <article className="flex flex-col gap-2 py-4 md:flex-row md:gap-5 md:py-5">
      <div className="flex shrink-0 items-center gap-2 md:w-24 md:flex-col md:items-start md:justify-start md:pt-1">
        <span className="text-sm font-bold text-foreground-950">{item.time}</span>
        <span className="hidden text-xs text-secondary-500 md:block">更新节点</span>
      </div>

      <div className="hidden w-px self-stretch bg-background-200 md:block"></div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.badge}`}>
            <i className={`${s.icon} text-xs`}></i>
            {item.state}
          </span>
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600">
            {item.category}
          </span>
          <span className="text-xs text-secondary-500">{item.refs}</span>
        </div>

        <h3 className="mt-2 text-base font-bold text-foreground-950 md:text-lg">{item.subject}</h3>
        <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.brief}</p>

        <button
          type="button"
          onClick={onToggle}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
        >
          <i className={`${expanded ? "ri-subtract-line" : "ri-add-line"} text-sm`}></i>
          {expanded ? "收起关联脉络" : "展开关联脉络"}
        </button>

        {expanded && (
          <div className="mt-3 rounded-lg border border-background-200 bg-background-100 p-4">
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-foreground-800">
              <i className="ri-node-tree text-sm text-primary-600"></i>
              时间线补全中 · 本次更新涉及
            </p>
            <div className="flex flex-wrap gap-2">
              {item.refs.split("·").map((ref) => (
                <span key={ref} className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-600">
                  {ref}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-secondary-600">
              该条九色黑料新进展仍在核实，后续若出现新的九色黑料爆料或官方回应，会自动在此时间线内追加节点，刷新首页即可看到九色黑料最新变化。
            </p>
          </div>
        )}
      </div>
    </article>
  );
}